import { FormEvent, useState } from "react";
import { Link, Location, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";

import { setActiveArtistId } from "@/api/client";
import { useAuth } from "@/lib/authContext";
import { Button } from "@/components/ui/button";

export default function LoginPage() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const location = useLocation();
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const response = await fetch("/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });
      if (!response.ok) {
        const errorPayload = await response.json().catch(() => null);
        const message = (errorPayload && (errorPayload.detail || errorPayload.message)) || "Authentication failed";
        throw new Error(message);
      }
      const data = await response.json();

      // 2FA pending — redirect to verification page
      if (data.pending_2fa && data.two_fa_session_id) {
        navigate("/two-factor", { state: { session_id: data.two_fa_session_id }, replace: true });
        return;
      }

      const normalizedPermissions = Array.isArray(data.permissions) ? data.permissions : [];
      const normalizedUser =
        data.user ??
        ({
          id: data.profile?.id ?? undefined,
          email: (data.profile?.email as string | undefined) ?? email,
          name:
            (data.profile?.display_name as string | undefined) ??
            (data.profile?.username as string | undefined) ??
            email,
          role: data.roles?.[0]?.role ?? (data.profile as { role?: string })?.role ?? "owner",
          permissions: normalizedPermissions,
        } as Record<string, unknown>);
      const sessionPayload = {
        ...data,
        user: normalizedUser,
        organization: data.organization ?? null,
        teams: Array.isArray(data.teams) ? data.teams : [],
        permissions: normalizedPermissions,
        email: (normalizedUser as { email?: string }).email,
        role: (normalizedUser as { role?: string }).role,
        display_name:
          (normalizedUser as { name?: string }).name ??
          (data.profile?.display_name as string | undefined) ??
          email,
        username:
          (data.profile?.username as string | undefined) ??
          (normalizedUser as { email?: string }).email ??
          (normalizedUser as { name?: string }).name ??
          email,
      };
      await completeLogin(data.token, sessionPayload);
      const nextArtistId = typeof data.current === "string" ? data.current : "";
      setActiveArtistId(nextArtistId);
      const fromLocation = (location.state as { from?: Location })?.from;
      const search = fromLocation?.search ?? "";
      const pathname = fromLocation?.pathname ?? "/dashboard";
      navigate(`${pathname}${search}`, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Authentication failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="launch-root flex min-h-screen items-center justify-center px-4 text-foreground">
      <div className="launch-hero-bg" aria-hidden="true" />
      <div className="launch-orb launch-orb--one" aria-hidden="true" />
      <div className="launch-orb launch-orb--two" aria-hidden="true" />
      <form
        className="aa-glass-card relative z-10 w-full max-w-md space-y-6 rounded-2xl p-8 shadow-xl"
        onSubmit={handleSubmit}
      >
        <h1 className="aa-page-title text-2xl font-semibold text-foreground">
          Agents Login
        </h1>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Email</label>
            <input
              name="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className="w-full rounded-md border border-border bg-background/80 px-3 py-2 text-sm text-foreground/80 focus:border-primary/50 focus:outline-none"
              autoComplete="email"
              required
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Password</label>
              <Link
                to="/forgot-password"
                className="text-xs text-muted-foreground hover:text-foreground hover:underline"
                tabIndex={-1}
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="login-password"
                name="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                className="w-full rounded-md border border-border bg-background/80 py-2 pl-3 pr-10 text-sm text-foreground/80 focus:border-primary/50 focus:outline-none"
                autoComplete="current-password"
                aria-label="Password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword((visible) => !visible)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                aria-controls="login-password"
                aria-pressed={showPassword}
                className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-rose-400">{error}</p>}

        <Button
          type="submit"
          className="w-full uppercase tracking-wide"
          disabled={loading || !email || !password}
        >
          {loading ? "Processing…" : "Sign in"}
        </Button>
      </form>
    </div>
  );
}
